import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    faqs = "faqs",
}

export interface FAQSection {
    fields: {
        question: string;
        answer: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.faqs]: FAQSection[];
    };
}

export interface FAQStyles {
    faqSection?: InjectableCss;
    faqBlock?: InjectableCss;
    questionButton?: InjectableCss;
    answerBlock?: InjectableCss;
}

const faqStyle: FAQStyles = {
    faqSection: {
        css: css`
            font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
        `,
    },
    faqBlock: {
        css: css`
            margin: 4px 2px;

            > div {
                display: grid;
                transition: grid-template-rows 200ms;
                > * {
                    overflow: hidden;
                    > * {
                        &:first-child {
                            margin-top: 10px;
                            margin-right: 18px;
                            margin-left: 18px;
                        }
                        &:last-child: {
                            margin-bottom: 10px;
                        }
                    }
                    > p {
                        font-size: 14px;
                    }
                }
            }

            .panel-open {
                grid-template-rows: 1fr;
            }

            .panel-close {
                grid-template-rows: 0fr;
            }
        `,
    },
    questionButton: {
        css: css`
            width: 100%;
            text-align: left;
            color: #000;
            background: #ffcc00;
            display: flex;
            flex-direction: row-reverse;
            justify-content: space-between;
            align-items: center;
            line-height: 1.2;
            // padding: 10px 21px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 10px 21px;
                        `,
                        css`
                            padding: 10px 21px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}

            &.button-expanded {
                &::before {
                    content: "-";
                    color: #000;
                    font-size: 25px;
                    float: right;
                }
            }

            &.button-collapsed {
                &::before {
                    content: "\002B";
                    font-size: 15px;
                    color: #000;
                    font-weight: bold;
                    float: right;
                }
            }

            &:active{
                border-color: transparent;
            }

            &:hover {
                background: #face3a;
            }
        `,
    },
    answerBlock: {
        css: css``,
    },
};

const styles = faqStyle;

const ButtonElement = ({ question, answer }: { question: string; answer: string }) => {
    const [showElement, setShowElement] = useState(false);

    return (
        <>
            <StyledWrapper {...styles.faqBlock}>
                <Button
                    variant="primary"
                    {...styles.questionButton}
                    className={showElement ? "button-expanded" : "button-collapsed"}
                    onClick={() => setShowElement(!showElement)}
                >
                    {question}
                </Button>
                <StyledWrapper {...styles.answerBlock} className={showElement ? "panel-open" : "panel-close"}>
                    <StyledWrapper>{parse(answer, parserOptions)}</StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const FAQSection = ({ fields }: OwnProps) => {
    return (
        <StyledWrapper {...styles.faqSection}>
            <StyledWrapper>
                {fields.faqs?.map((faq, index) => (
                    <StyledWrapper key={`${index}_${faq.fields.question}`}>
                        <ButtonElement question={faq.fields.question} answer={faq.fields.answer} />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const FAQModule: WidgetModule = {
    component: FAQSection,
    definition: {
        displayName: "FAQ Section",
        fieldDefinitions: [
            {
                name: fields.faqs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.question;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "question",
                        displayName: "Question",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "answer",
                        editorTemplate: "RichTextField",
                        displayName: "Answer",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
        group: "Basic",
    },
};

export default FAQModule;
